import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useCampaignLink } from '../hooks/useCampaignLink';
import { logDebug, logError } from '../services/loggerService';

/**
 * Resolver page for `/r/<ref>` — campaign short links, both the ones marketing
 * shares (email, social, SMS) and the ones printed as QR codes.
 *
 * Flow:
 *   1. Page mounts at /r/<ref>. GET /r/<ref> is PUBLIC; it counts the open and
 *      returns the campaign's landing URL as of now (target + UTM tuple +
 *      language, or the campaign's archive redirect).
 *   2. window.location.replace(targetUrl) — a FULL navigation, always: the UTM
 *      capture (utmService) reads the landing URL on load, and the target may
 *      be outside the app. replace() keeps /r/<ref> out of the back stack.
 *   3. 404 / 5xx → land at home; the global error modal surfaces the cause.
 *
 * Link-preview fetchers (iMessage, Slack) never run this page, so they are
 * never counted as opens.
 */
export const CampaignLinkPage: React.FC = () => {
  const { ref } = useParams<{ ref: string }>();
  const history = useHistory();
  const { data, error, isFetching } = useCampaignLink(ref);

  useEffect(() => {
    if (!data) return;
    if (!data.targetUrl) {
      logDebug('CampaignLink: no targetUrl', undefined, { ref });
      history.replace('/');
      return;
    }
    logDebug('CampaignLink: full navigation to targetUrl', undefined, { ref });
    window.location.replace(data.targetUrl);
  }, [data, history, ref]);

  useEffect(() => {
    if (!error) return;
    logError(error instanceof Error ? error : new Error(String(error)), 'CampaignLink: failed to resolve', undefined, {
      ref,
      status: (error as { status?: number }).status,
    });
    history.replace('/');
  }, [error, history, ref]);

  return (
    <IonPage>
      <IonContent>
        <div className="flex h-full items-center justify-center">
          {(isFetching || !!data) && <IonSpinner name="crescent" />}
        </div>
      </IonContent>
    </IonPage>
  );
};
